import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Companies } from '@etoh/database/angular';
import { Contacts } from '@etoh/database/core';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { UserService } from 'apps/desktop/src/app/user.service';
import { BehaviorSubject, filter, firstValueFrom, map, of, take } from 'rxjs';
import { AddressesService } from '../../../addresses/addresses.service';
import { AddressesAddComponent } from '../../../addresses/containers/addresses-add/addresses-add.component';
import { CompaniesService } from '../../../companies/companies.service';
import { CompanyAddComponent } from '../../../companies/containers/company-add/company-add.component';
import { EntitiesService } from '../../../entities/entities.service';
import { PageFormComponent } from '../../../shared/components/layout/page-form/page-form.component';
import { FormsService } from '../../../shared/forms/forms.service';
import { CommentsNotesFormAndAttachedFilesForm } from '../../../shared/forms/templates.forms';
import {
  PageFormButton,
  PageFormInterface,
  PageFormMode,
} from '../../../shared/interfaces/pageForm.interface';
import { TabsTable } from '../../../shared/interfaces/tabsTable.interface';
import { StoreService } from '../../../store.service';
import { UsersService } from '../../../users.service';

import { NzMessageService } from 'ng-zorro-antd/message';
import { emailsForInvitation } from './emails';

@Component({
  selector: 'etoh-contact-add',
  templateUrl: './contact-add.component.html',
  styleUrls: ['./contact-add.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [PageFormComponent],
})
export class ContactAddComponent implements OnInit, PageFormInterface {
  @ViewChild(PageFormComponent, { static: false }) pageForm: PageFormComponent;
  @Input() id: number;
  @Input() standalone = true;

  options: FormlyFormOptions = {};

  @Input() title?: string;

  fields: FormlyFieldConfig[];

  onChange = new BehaviorSubject<keyof Contacts | null>(null);

  @Input() model = {};

  dataTabs: TabsTable[];
  headerButtons: PageFormButton[];

  private usersService = inject(UsersService);
  private companiesService = inject(CompaniesService);
  private addressesService = inject(AddressesService);
  private storeService = inject(StoreService);
  private formsService = inject(FormsService);
  private userService = inject(UserService);
  private entitiesService = inject(EntitiesService);
  private cd = inject(ChangeDetectorRef);
  private message = inject(NzMessageService);

  ngOnInit(): void {
    this.fields = [
      {
        fieldGroupClassName: 'ant-row layout-padding',
        fieldGroup: [
          {
            key: 'firstname',
            type: 'input',
            className: 'ant-col ant-col-third',
            props: {
              label: 'First name',
              placeholder: '',
              required: true,
            },
          },
          {
            key: 'lastname',
            type: 'input',
            className: 'ant-col ant-col-third',
            props: {
              label: 'Last name',
              placeholder: '',
              required: true,
            },
          },
          {
            key: 'companyId',
            type: 'select',
            className: 'ant-col ant-col-third',
            props: {
              label: 'Company',
              placeholder: '',
              tooltip: this.formsService.getTooltip(
                'companies',
                CompanyAddComponent
              ),
              required: true,
              options: this.companiesService.getCompaniesForOptions$(),
              footer: this.formsService.getFooterSelect(
                'companies',
                CompanyAddComponent
              ),
              change: () => {
                console.log('change companyID');
                this.onChange.next('companyId');
              },
            },
          },
          {
            key: 'jobTitle',
            type: 'input',
            className: 'ant-col ant-col-third',
            props: {
              label: 'Job title',
              placeholder: '',
              required: false,
            },
          },
          {
            key: 'phone',
            type: 'phone',
            className: 'ant-col ant-col-third',
            props: {
              label: 'Phone',
              placeholder: '',
              required: false,
            },
          },
          {
            key: 'mobile',
            type: 'phone',
            className: 'ant-col ant-col-third',
            props: {
              label: 'Mobile',
              placeholder: '',
              required: false,
            },
          },
          {
            key: 'officePhone',
            type: 'phone',
            className: 'ant-col ant-col-third',
            props: {
              label: 'Office Phone',
              placeholder: '',
              required: false,
            },
          },
          {
            key: 'altPhone',
            type: 'phone',
            className: 'ant-col ant-col-third',
            props: {
              label: 'Alt. Phone',
              placeholder: '',
              required: false,
            },
          },
          {
            key: 'fax',
            type: 'input',
            className: 'ant-col ant-col-third',
            props: {
              label: 'Fax',
              placeholder: '',
              required: false,
            },
          },
          {
            key: 'email',
            type: 'input',
            className: 'ant-col ant-col-third',
            props: {
              label: 'Email',
              type: 'email',
              placeholder: 'john@doe.com',
              required: false,
            },
            validators: {
              validation: ['email'],
            },
          },
          {
            key: 'altEmail',
            type: 'input',
            className: 'ant-col ant-col-third',
            props: {
              label: 'Alt. Email',
              type: 'email',
              placeholder: 'john2@doe.com',
              required: false,
            },
            validators: {
              validation: ['email'],
            },
          },
          {
            key: 'categories',
            type: 'tags',
            className: 'ant-col ant-col-third',
            hide: this.userService.role$.value === 'guest',
            props: {
              label: 'Contact categories',
              placeholder: '',
              required: false,
              options: this.formsService.getOptionsFromPreviousItems(
                'contacts',
                'categories'
              ),
            },
          },
          {
            key: 'addressId',
            type: 'select',
            className: 'ant-col ant-col-third',
            expressions: {
              'props.disabled': '!model.companyId',
            },
            props: {
              label: 'Address',
              placeholder: '',
              required: false,
              allowClear: true,
              footer: this.formsService.getFooterSelect(
                'addresses',
                AddressesAddComponent,
                () => {
                  return {
                    companyId: this.pageForm.model.companyId,
                  };
                }
              ),
              tooltip: this.formsService.getTooltip(
                'addresses',
                this.userService.role$.value === 'guest'
                  ? null
                  : AddressesAddComponent
              ),
            },
            hooks: {
              onInit: (field) => {
                this.formsService.setOptionsDependantOf(
                  field,
                  ['companyId'],
                  ([companyId]) => {
                    if (!companyId) {
                      return of([]);
                    }

                    return this.addressesService
                      .getAddressesForCompany$(companyId)
                      .pipe(
                        map((addresses) =>
                          this.addressesService.getAddressesForOptions(
                            addresses
                          )
                        )
                      );
                  },
                  this.onChange.pipe(filter((key) => key === 'companyId'))
                );
              },
            },
          },
          {
            key: 'brokerId',
            type: 'select',
            className: 'ant-col ant-col-third',
            defaultValue: this.userService.userId$.value,
            props: {
              label: 'Broker',
              required: false,
              options: this.usersService.getBrokersForOptions$(),
            },
          },
        ],
      },
      ...(this.userService.role$.value === 'guest'
        ? []
        : CommentsNotesFormAndAttachedFilesForm),
    ];
  }

  public onLoaded() {
    if (
      this.userService.userBehaviourSubject$.value?.role === 'guest' &&
      this.pageForm?.id
    ) {
      const onlyFields: Partial<keyof Contacts>[] = [
        'altEmail',
        'altPhone',
        'mobile',
        'officePhone',
      ];

      this.pageForm.keysOnlyForSaving = onlyFields;

      this.options.formState = {
        ...this.options.formState,
        onlyFields,
      };

      this.options?.build?.();
    }

    if (this.pageForm?.id) {
      this.dataTabs = [
        {
          title: 'Companies',
          dataType: 'companies',
          datas$: this.storeService
            .getStoreByKeys$<Companies>('companies')
            .pipe(
              map((companiesByKey) => {
                return [companiesByKey[this.pageForm.model.companyId]];
              })
            ),
        },
      ];

      this.headerButtons = [
        {
          label: 'Invite the contact to the platform',
          roles: ['user'],
          visibity: ['edit'],
          click: () => {
            this.openMailToLink();
          },
          buttons: [
            {
              label: 'English email',
              click: () => {
                this.openMailToLink('en');
              },
            },
            {
              label: 'French email',
              click: () => {
                this.openMailToLink('fr');
              },
            },
            {
              label: 'Spanish email',
              click: () => {
                this.openMailToLink('es');
              },
            },
          ],
        },
      ];
    }
  }

  // Check if name is duplicated
  shouldPrevent = (model: any, mode: PageFormMode) => {
    if (mode === 'edit') {
      return of(false);
    }
    return this.entitiesService.getCorrespondanceAndShowDialog<Contacts>(
      'contacts',
      ['firstname', 'lastname'],
      [model?.firstname, model?.lastname].filter((v) => !!v).join(' ')
    );
  };

  private getMailtoLink(lang: string) {
    const { model } = this.pageForm;

    const data: { subject: string; body: string } =
      (emailsForInvitation as any)[lang] ?? emailsForInvitation['en'];

    const mailto = `mailto:${model.email}?subject=${
      data.subject
    }&body=${this.transformBodyEmail(data.body, model)}`;
    return mailto;
  }

  public async openMailToLink(lang = 'en') {
    const companiesById = await firstValueFrom(
      this.storeService.getStoreByKeys$<Companies>('companies').pipe(take(1))
    );

    const companyId = this.pageForm.model.companyId;
    const company = companiesById[companyId];

    if (!company?.officeId) {
      this.message.create(
        'error',
        'Before inviting the contact, please add an office to the company of the contact'
      );
      return;
    }

    const mailto = this.getMailtoLink(lang);
    window.open(mailto, '_blank');
  }

  private transformBodyEmail(body: string, model: any): string {
    const urlForRegistering = `${window.location.protocol}//${window.location.hostname}/auth/register?email=${model.email}`;

    return body
      .replace(/\n/g, '%0D%0A')
      .replace(
        '[name]',
        [model.firstname, model.lastname].filter((v) => !!v).join(' ')
      )
      .replace('[url]', urlForRegistering)
      .replace('[email]', model.email)
      .replace(/&/g, '%26');
  }
}
